import React from "react";
import { NavLink } from "react-router-dom";
import {
  X,
  LayoutDashboard,
  Users,
  FileCode,
  CalendarClock,
  Laptop,
  DollarSign,
  CalendarDays,
  Sun,
} from "lucide-react";
import { ENTITY_NAMES } from "../../constants/entities";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 md:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-primary-800XXX transition duration-300 ease-in-out md:translate-x-0 md:static md:inset-auto md:h-screen ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <svg
          className="m-5 p-8 bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          viewBox="0 0 134.2 82.5"
        >
          <g>
            <path
              className="st1"
              d="M7.4,70.5c-2,0-3.6-.6-4.9-1.9-1.3-1.3-1.9-2.8-1.9-4.6s.6-3.5,1.9-4.7,2.9-1.8,4.7-1.8c1.6,0,2.9.3,3.9,1.1s1.6,1.7,1.6,2.8-.2.8-.5,1.1c-.3.3-.8.4-1.4.4-.6,0-1.1-.3-1.5-.7-.4-.4-.7-.9-.9-1.4-.2-.5-.5-1-.8-1.4s-.7-.6-1.1-.6c-.5,0-1,.3-1.3.9-.3.6-.5,1.2-.5,2.1,0,1.7.4,3.1,1.2,4.1.8,1,1.8,1.5,3.1,1.5s2.1-.5,2.9-1.5c.2-.2.4-.3.6-.2.2,0,.4.2.4.5,0,1.2-.5,2.3-1.5,3.1s-2.3,1.3-4,1.3Z"
            />
            <path
              className="st1"
              d="M20.9,70.5c-2,0-3.7-.6-5.1-1.9-1.4-1.2-2.1-2.8-2.1-4.7s.7-3.4,2.1-4.7c1.4-1.2,3-1.8,5-1.8s3.7.6,5.1,1.9c1.4,1.2,2.1,2.8,2.1,4.7s-.7,3.4-2,4.7c-1.4,1.2-3,1.8-4.9,1.8ZM21.5,69.2c1.1,0,1.7-1.1,1.7-3.2s-.1-1.7-.3-2.8c-.2-1.1-.6-2.1-1.1-3.1s-1.1-1.4-1.7-1.4-1,.3-1.3.8c-.3.5-.4,1.4-.4,2.5s0,1.3.2,2c.1.8.3,1.6.6,2.4.2.8.6,1.5,1,2s.9.8,1.4.8Z"
            />
            <path
              className="st1"
              d="M42.1,67.4c0,.4.2.9.5,1.3s.5.7.5,1h0c0,.4-.2.6-.6.6h-3.7c-.5,0-.7-.2-.7-.7v-.2c-.8.8-1.9,1.2-3.2,1.2s-3.1-.6-4.3-1.7c-1.2-1.2-1.8-2.8-1.8-4.8s.6-3.5,1.8-4.7c1.2-1.2,2.7-1.8,4.4-1.8s2,.2,2.7.7v-3.5c0-.3-.1-.6-.3-.9-.2-.3-.5-.5-.7-.7-.2-.2-.4-.4-.4-.6h0c0-.4.2-.6.6-.8l4.1-1.2c.8-.2,1.1,0,1.1.6v16.2ZM36,69.2c.5,0,.9-.2,1.2-.6.3-.4.5-.9.5-1.6v-5c0-.8-.2-1.6-.7-2.3-.5-.7-1-1-1.6-1-1.3,0-2,1.1-2.1,3.2,0,.8,0,1.8.2,2.8.2,1.1.5,2.1.9,3,.4.9,1,1.4,1.6,1.4Z"
            />
            <path
              className="st1"
              d="M54.7,66.1c.2-.2.4-.2.6-.2.2,0,.3.3.3.5,0,.9-.5,1.8-1.5,2.7-1,.9-2.2,1.4-3.8,1.4s-3.4-.6-4.7-1.8c-1.2-1.2-1.8-2.8-1.8-4.8s.6-3.4,1.9-4.6c1.3-1.2,2.8-1.8,4.5-1.8s2.8.4,3.7,1.1c.9.7,1.3,1.6,1.3,2.5s-.3,1.3-1,1.6l-5.2,2.9c.7,1.1,1.7,1.7,3,1.7s1-.1,1.5-.3c.5-.2.8-.5,1.1-.8ZM49.9,58.7c-.5,0-.9.3-1.3.8-.3.5-.5,1.2-.5,2s.1,2,.4,2.7l2.4-1.5c.5-.3.7-.8.7-1.3s-.1-1.2-.4-1.8-.7-.9-1.3-.9Z"
            />
            <path
              className="st0"
              d="M64.8,57.4c1.6,0,3.1.6,4.3,1.7,1.2,1.1,1.9,2.7,1.9,4.6s-.6,3.6-1.8,4.9c-1.2,1.3-2.7,1.9-4.4,1.9s-2.3-.4-3.2-1.2v.2c0,.5-.2.7-.7.7h-3.9c-.4,0-.6-.2-.6-.5h0c0-.3.2-.6.5-1.1s.5-.9.5-1.3v-11.9c0-.5-.2-.9-.6-1.3-.4-.4-.6-.7-.6-.9h0c0-.4.2-.6.7-.8l4-1.9c.3-.2.6-.2.9-.1s.4.3.4.7v7c.7-.5,1.6-.8,2.7-.8ZM64.4,69.2c1.3,0,2-1.1,2-3.3s0-2.1-.2-3.1c-.2-1-.5-2-.9-2.8s-.9-1.3-1.5-1.3-.8.2-1.1.6-.4,1-.4,1.7v5.1c0,.8.2,1.5.7,2.2.5.6,1,1,1.6,1Z"
            />
            <path
              className="st0"
              d="M72.6,70.3c-.4,0-.6-.2-.6-.5h0c0-.3.2-.6.5-1.1.3-.4.5-.9.5-1.3v-11.9c0-.5-.2-.9-.5-1.3-.3-.4-.5-.7-.5-.9h0c0-.3.2-.5.6-.7l3.9-1.9c.4-.2.7-.2.9,0s.3.3.3.6v16.2c0,.4.2.9.6,1.3s.6.7.6,1h0c0,.4-.2.6-.6.6h-5.6Z"
            />
            <path
              className="st0"
              d="M87.2,70.5c-2,0-3.7-.6-5.1-1.9-1.4-1.2-2.1-2.8-2.1-4.7s.7-3.4,2.1-4.7c1.4-1.2,3-1.8,5-1.8s3.7.6,5.1,1.9c1.4,1.2,2.1,2.8,2.1,4.7s-.7,3.4-2,4.7c-1.4,1.2-3,1.8-4.9,1.8ZM87.7,69.2c1.1,0,1.7-1.1,1.7-3.2s-.1-1.7-.3-2.8c-.2-1.1-.6-2.1-1.1-3.1s-1.1-1.4-1.7-1.4-1,.3-1.3.8c-.3.5-.4,1.4-.4,2.5s0,1.3.2,2c.1.8.3,1.6.6,2.4.2.8.6,1.5,1,2s.9.8,1.4.8Z"
            />
            <path
              className="st0"
              d="M102.5,70.5c-2,0-3.7-.6-5.1-1.9-1.4-1.2-2.1-2.8-2.1-4.7s.7-3.4,2.1-4.7c1.4-1.2,3-1.8,5-1.8s3.7.6,5.1,1.9c1.4,1.2,2.1,2.8,2.1,4.7s-.7,3.4-2,4.7c-1.4,1.2-3,1.8-4.9,1.8ZM103.1,69.2c1.1,0,1.7-1.1,1.7-3.2s-.1-1.7-.3-2.8c-.2-1.1-.6-2.1-1.1-3.1s-1.1-1.4-1.7-1.4-1,.3-1.3.8c-.3.5-.4,1.4-.4,2.5s0,1.3.2,2c.1.8.3,1.6.6,2.4.2.8.6,1.5,1,2s.9.8,1.4.8Z"
            />
            <path
              className="st0"
              d="M132.9,67.4c0,.4.2.9.5,1.3s.4.8.4,1h0c0,.4-.2.6-.6.6h-5.2c-.4,0-.6-.2-.6-.5h0c0-.3.2-.6.5-1 .3-.4.5-.9.4-1.3l-.3-4.6c0-1.3-.2-2.2-.5-2.8-.3-.5-.7-.8-1.3-.8s-.9.2-1.2.7c-.3.5-.4,1.1-.4,1.8,0,.4,0,.7,0,1.1v4.7c0,.4.2.9.5,1.3s.5.8.5,1h0c0,.4-.2.6-.6.6h-5.2c-.4,0-.6-.2-.6-.5h0c0-.3.2-.6.5-1.1s.5-.9.5-1.3v-5c0-2.2-.6-3.3-1.8-3.3s-1,.2-1.3.7c-.3.5-.4,1.1-.4,1.8v5.7c0,.4.2.9.6,1.3s.6.8.6,1h0c0,.4-.2.6-.6.6h-5.6c-.4,0-.6-.2-.6-.5h0c0-.3.2-.6.6-1.1.4-.4.6-.9.6-1.3v-5.1c0-.3-.1-.6-.4-.9-.2-.3-.5-.5-.7-.7s-.3-.4-.3-.6h0c0-.3.2-.5.6-.7l3.9-1.8c.3-.1.6-.1.7,0,.2.1.3.3.3.6v.9c.8-1.1,2-1.7,3.5-1.7s3.4.7,4.3,2.1c.3-.7.9-1.2,1.5-1.6.7-.4,1.5-.6,2.3-.6,1.3,0,2.4.4,3.3,1.3.9.9,1.4,2.3,1.6,4.2l.3,4.5Z"
            />
          </g>
          <path
            className="st1"
            d="M67.1,36.4c-.9,0-1.5-.7-1.5-1.5v-13.4c0-.8.7-1.5,1.5-1.5s1.5.7,1.5,1.5v13.4c0,.8-.7,1.5-1.5,1.5Z"
          />
          <path
            className="st1"
            d="M43.3,39.6c-1.1,0-2.1-.4-2.9-1.2s-1.2-1.7-1.2-2.9v-6.7c0-.4-.1-.7-.4-1-.3-.3-.6-.4-1-.4s-.7-.1-1-.4c-.3-.3-.4-.6-.4-1s.1-.7.4-1c.3-.3.6-.4,1-.4s.7-.1,1-.4c.3-.3.4-.6.4-1v-6.7c0-1.1.4-2.1,1.2-2.9.8-.8,1.7-1.2,2.9-1.2s.7.1,1,.4.4.6.4,1-.1.7-.4,1c-.3.3-.6.4-1,.4s-.7.1-1,.4c-.3.3-.4.6-.4,1v6.7c0,1-.3,1.9-1,2.7h0c.7.8,1,1.7,1,2.7v6.7c0,.4.1.7.4,1,.3.3.6.4,1,.4s.7.1,1,.4c.3.3.4.6.4,1s-.1.7-.4,1c-.3.3-.6.4-1,.4Z"
          />
          <path
            className="st1"
            d="M90.9,39.6c-.4,0-.7-.1-1-.4-.3-.3-.4-.6-.4-1s.1-.7.4-1c.3-.3.6-.4,1-.4s.7-.1,1-.4c.3-.3.4-.6.4-1v-6.7c0-1.1.3-1.9,1-2.7h0c-.7-.8-1-1.7-1-2.7v-6.7c0-.4-.1-.7-.4-1-.3-.3-.6-.4-1-.4s-.7-.1-1-.4c-.3-.3-.4-.6-.4-1s.1-.7.4-1c.3-.3.6-.4,1-.4,1.1,0,2.1.4,2.9,1.2.8.8,1.2,1.7,1.2,2.9v6.7c0,.4.1.7.4,1s.6.4,1,.4.7.1,1,.4c.3.3.4.6.4,1s-.1.7-.4,1c-.3.3-.6.4-1,.4s-.7.1-1,.4c-.3.3-.4.6-.4,1v6.7c0,1.1-.4,2.1-1.2,2.9-.8.8-1.7,1.2-2.9,1.2Z"
          />
          <path
            className="st1"
            d="M80.4,20.9c-2.9,0-5.2,2.3-5.2,5.2s2.3,5.2,5.2,5.2,5.2-2.3,5.2-5.2-2.3-5.2-5.2-5.2ZM80.4,28c-1,0-1.9-.8-1.9-1.9s.8-1.9,1.9-1.9,1.9.8,1.9,1.9-.8,1.9-1.9,1.9Z"
          />
          <path
            className="st1"
            d="M52.1,27.9h-1.7c-.5,0-.9-.2-1.3-.5-.3-.3-.5-.8-.5-1.2s.2-.9.5-1.2c.3-.3.8-.5,1.3-.5h1.7v-1.7c0-.5.2-.9.5-1.2s.8-.5,1.3-.5.9.2,1.2.5c.3.3.5.8.5,1.2v1.7h1.7c.5,0,.9.2,1.2.5.3.3.5.8.5,1.2s-.2.9-.5,1.2-.8.5-1.2.5h-1.7v1.7c0,.5-.2.9-.5,1.2-.3.3-.8.5-1.2.5s-.9-.2-1.3-.5-.5-.8-.5-1.2v-1.7Z"
          />
          <path
            className="st1"
            d="M66.9,45.2c-2.9,0-5.5-.7-7.6-1.9-.2-.1-.6-.4-1.1-.7-.3-.3-.5-.7-.5-1s.1-.7.4-1,.6-.4,1-.4.7.1,1,.4c.3.2.6.5,1,.6,1.6.8,3.5,1.2,5.7,1.2s4.2-.4,5.7-1.2c.5-.2.9-.6,1.3-1,.3-.3.6-.4,1-.4s.7.1,1,.4c.3.3.4.6.4,1s-.1.7-.4,1c-.6.5-1.1.8-1.5,1-2.1,1.3-4.6,1.9-7.6,1.9Z"
          />
          <path
            className="st1"
            d="M55.2,11.3c-1.2-1.2-1.9-2.8-1.9-4.5s.7-3.3,1.9-4.5c1.2-1.2,2.8-1.9,4.5-1.9s3.3.7,4.5,1.9l2.9,2.9,2.9-2.9c1.2-1.2,2.8-1.9,4.5-1.9s3.3.7,4.5,1.9c2.5,2.5,2.5,6.5,0,9l-11.9,11.9-11.9-11.9ZM67.1,18.7l9.7-9.7c1.3-1.3,1.3-3.3,0-4.6s-1.4-1-2.3-1-1.7.3-2.3,1l-5.1,5.1-5.1-5.1c-.6-.6-1.4-1-2.3-1s-1.7.3-2.3,1c-.6.6-1,1.4-1,2.3s.3,1.7,1,2.3h0l9.7,9.7Z"
          />
          <g>
            <path className="st1" d="M37,82.3v-6.5h1v5.6h3.1v.9h-4.1Z" />
            <path
              className="st1"
              d="M54.5,82.3l1.9-6.5h1.8l1.8,6.5h-1l-.4-1.5h-2.5l-.4,1.5h-1ZM56.2,79.9h2l-.9-3.4h-.1l-.9,3.4Z"
            />
            <path
              className="st1"
              d="M73.6,82.3v-.9h.9v-4.7h-.9v-.9h3.3c.4,0,.7,0,1,.2s.5.3.7.6c.2.2.2.5.2.9h0c0,.4,0,.6-.2.8s-.2.3-.4.4c-.2.1-.3.2-.4.2h0c.1.2.3.3.5.4s.3.3.4.4.2.4.2.8h0c0,.5,0,.8-.3,1-.2.3-.4.4-.7.6-.3.1-.6.2-1,.2h-3.3ZM75.5,78.6h1.3c.3,0,.6,0,.8-.2.2-.2.3-.4.3-.6h0c0-.4,0-.6-.3-.7-.2-.2-.4-.2-.8-.2h-1.3v1.9ZM75.5,81.4h1.3c.3,0,.6,0,.8-.2.2-.2.3-.4.3-.7h0c0-.4,0-.6-.3-.8-.2-.2-.5-.2-.8-.2h-1.3v1.9Z"
            />
            <path
              className="st1"
              d="M95.2,82.4c-.5,0-.9,0-1.3-.3s-.7-.4-.9-.8c-.2-.3-.3-.7-.3-1.2v-.2h1v.2c0,.5.1.8.4,1,.3.2.6.3,1.1.3s.8,0,1-.3c.2-.2.4-.4.4-.8s0-.4-.2-.5-.3-.2-.5-.3c-.2,0-.4-.1-.7-.2h-.3c-.4-.2-.8-.3-1.1-.4-.3-.1-.5-.3-.7-.6s-.2-.5-.2-.9,0-.7.3-1,.5-.5.8-.6c.3-.1.7-.2,1.2-.2s.9,0,1.2.2.6.4.8.7.3.7.3,1.1v.3h-1v-.3c0-.3,0-.5-.2-.7s-.3-.3-.5-.4c-.2,0-.4-.1-.7-.1-.4,0-.7,0-.9.2-.2.2-.3.4-.3.7s0,.4.1.5c0,.1.2.2.4.3.2,0,.4.2.7.2h.3c.4.2.8.3,1.1.4.3.1.6.3.8.6.2.2.3.6.3,1s0,.7-.3,1-.5.5-.8.7c-.4.2-.8.2-1.3.2Z"
            />
          </g>
        </svg>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-primary-200 hover:bg-neutral-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white md:hidden"
        >
          <X className="h-6 w-6" />
        </button>

        <nav className="m-5 p-4XXX bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <LayoutDashboard className="mr-3 h-5 w-5 flex-shrink-0" />
            Dashboard
          </NavLink>

          <NavLink
            to="/students"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <Users className="mr-3 h-5 w-5 flex-shrink-0" />
            Students
          </NavLink>

          <NavLink
            to="/series"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <FileCode className="mr-3 h-5 w-5 flex-shrink-0" />
            {ENTITY_NAMES.SERIES}
          </NavLink>

          <NavLink
            to="/labs"
            className={({ isActive }) =>
              `flex items-center pl-10 pr-4 py-2.5 border-t border-neutral-100 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <Laptop className="mr-3 h-5 w-5 flex-shrink-0" />
            {ENTITY_NAMES.LAB}s
          </NavLink>

          <NavLink
            to="/events"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <CalendarDays className="mr-3 h-5 w-5 flex-shrink-0" />
            Events
          </NavLink>

          <NavLink
            to="/camps"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <Sun className="mr-3 h-5 w-5 flex-shrink-0" />
            Camps
          </NavLink>

          <NavLink
            to="/runs"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <CalendarClock className="mr-3 h-5 w-5 flex-shrink-0" />
            {ENTITY_NAMES.RUN}s
          </NavLink>

          <NavLink
            to="/payments"
            className={({ isActive }) =>
              `flex items-center px-4 py-4 border-t border-neutral-200 transition-colors ${
                isActive
                  ? "bg-light text-main"
                  : "text-main hover:bg-light hover:text-main"
              }`
            }
          >
            <DollarSign className="mr-3 h-5 w-5 flex-shrink-0" />
            {ENTITY_NAMES.PAYMENT}s
          </NavLink>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="rounded-md bg-primary-700 p-3 text-center text-xs text-primary-200">
            <p>Codebloom Labs CRM</p>
            <p className="mt-1 text-xs">v1.0.0</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
